import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { parseInput, cleanItems } from '../../pairwise-ranking/utils/inputParsers';

interface ItemInputProps {
  onStart: (items: string[]) => void;
}

export function ItemInput({ onStart }: ItemInputProps) {
  const [items, setItems] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [useTextarea, setUseTextarea] = useState(false);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      const cleaned = cleanItems([...items, trimmed]);
      setItems(cleaned);
      setInputValue('');
    }
  };

  const handlePaste = (text: string) => {
    try {
      const parsed = parseInput(text, 'free-text');
      const cleaned = cleanItems(parsed);
      setItems(cleaned);
      setInputValue('');
    } catch (error) {
      alert(`Error parsing input: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRemove = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !useTextarea) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleTextareaPaste = (e: React.ClipboardEvent) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\n') || pastedText.includes(',')) {
      e.preventDefault();
      handlePaste(pastedText);
    }
  };

  const canStart = items.length >= 1;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">
          Add Items to Score
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Enter the items you want to prioritize using RICE scoring. You can paste a list or type items one at a time.
        </p>

        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setUseTextarea(!useTextarea)}
            className={`px-3 py-1 text-sm  transition-colors ${
              useTextarea
                ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {useTextarea ? 'Single Item Mode' : 'Paste List Mode'}
          </button>
        </div>

        {!useTextarea && (
          <div className="flex gap-2 mb-6">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter an item name..."
              className="flex-1 px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleAdd}
              disabled={!inputValue.trim()}
              className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add
            </button>
          </div>
        )}

        {useTextarea && (
          <div className="mb-6">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPaste={handleTextareaPaste}
              placeholder="Paste your list here (one per line or comma-separated)..."
              className="w-full px-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] font-mono text-sm"
            />
            <div className="mt-2">
              <button
                onClick={() => handlePaste(inputValue)}
                disabled={!inputValue.trim()}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed text-sm"
              >
                Parse & Add Items
              </button>
            </div>
          </div>
        )}

        {items.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">
              Items ({items.length})
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-[var(--surface)] border border-[var(--rule)]"
                >
                  <span className="text-[var(--text-pri)]">{item}</span>
                  <button
                    onClick={() => handleRemove(index)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => onStart(items)}
          disabled={!canStart}
          className="w-full px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed font-medium text-lg transition-colors"
        >
          {canStart
            ? `Start Scoring (${items.length} items)`
            : 'Add at least 1 item to start'}
        </button>
      </div>
    </div>
  );
}


